import { memo } from 'react';
import { cn } from '@/lib/utils';
import { ShoppingCart, Calendar } from 'lucide-react';

interface CatalogModeToggleProps {
    mode: 'rent' | 'buy';
    itemsForSaleCount: number;
    onChange: (mode: 'rent' | 'buy') => void;
}

export const CatalogModeToggle = memo(({ mode, itemsForSaleCount, onChange }: CatalogModeToggleProps) => {
    if (itemsForSaleCount === 0) {
        return null;
    }

    return (
        <div
            className="inline-flex items-center gap-1 rounded-full border border-border/80 bg-white/90 p-1.5 shadow-card"
            role="tablist"
            aria-label="Catalog mode"
        >
            <button
                type="button"
                role="tab"
                aria-selected={mode === 'rent'}
                aria-controls="catalog-content"
                onClick={() => onChange('rent')}
                className={cn(
                    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    mode === 'rent'
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
            >
                <Calendar className="h-4 w-4" aria-hidden="true" />
                <span>Rent</span>
            </button>
            <button
                type="button"
                role="tab"
                aria-selected={mode === 'buy'}
                aria-controls="catalog-content"
                onClick={() => onChange('buy')}
                className={cn(
                    'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all',
                    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                    mode === 'buy'
                        ? 'bg-accent text-accent-foreground shadow-sm'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                )}
            >
                <ShoppingCart className="h-4 w-4" aria-hidden="true" />
                <span>Buy pre-loved gear</span>
                <span className={cn(
                    'ml-1 inline-flex min-w-[24px] items-center justify-center rounded-full px-2 py-0.5 text-xs font-bold',
                    mode === 'buy' ? 'bg-black/10 text-current' : 'bg-primary/10 text-primary'
                )}>
                    {itemsForSaleCount}
                </span>
            </button>
        </div>
    );
});

CatalogModeToggle.displayName = 'CatalogModeToggle';
